import React, { useState } from 'react'
import { useApp } from '../../context/AppContext'
import { UserCheck, ArrowRight, Loader2, Calculator, Shield } from 'lucide-react'

function UserDetailsForm({ onComplete }) {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    height_cm: '',
    weight_kg: ''
  })
  const [errors, setErrors] = useState({})
  const { updateUserProfile, isLoading } = useApp()

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.age) {
      newErrors.age = 'Age is required'
    } else if (formData.age < 1 || formData.age > 120) {
      newErrors.age = 'Age must be between 1 and 120'
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required'
    }

    if (!formData.height_cm) {
      newErrors.height_cm = 'Height is required'
    } else if (formData.height_cm < 50 || formData.height_cm > 300) {
      newErrors.height_cm = 'Height must be between 50 and 300 cm'
    }

    if (!formData.weight_kg) {
      newErrors.weight_kg = 'Weight is required'
    } else if (formData.weight_kg < 20 || formData.weight_kg > 300) {
      newErrors.weight_kg = 'Weight must be between 20 and 300 kg'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await updateUserProfile(formData)
      onComplete()
    } catch (error) {
      console.error('Failed to update profile:', error)
    }
  }

  const calculateBMI = () => {
    const height = parseFloat(formData.height_cm)
    const weight = parseFloat(formData.weight_kg)
    
    if (height && weight) {
      const heightM = height / 100
      const bmi = weight / (heightM * heightM)
      return bmi.toFixed(1)
    }
    return null
  }

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' }
    if (bmi < 25) return { category: 'Normal', color: 'text-green-600' }
    if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' }
    return { category: 'Obese', color: 'text-red-600' }
  }

  const bmi = calculateBMI()
  const bmiInfo = bmi ? getBMICategory(bmi) : null

  return (
    <div className="flex justify-center">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-md w-full p-4">
        <div className="text-center mb-4">
          <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
            <UserCheck className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            Complete Your Profile
          </h2>
          <p className="text-gray-600 text-sm">
            Help us provide more accurate health analysis
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.name ? 'border-red-500' : ''}`}
                required
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Age */}
            <div>
              <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                Age *
              </label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                placeholder="25"
                min="1"
                max="120"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.age ? 'border-red-500' : ''}`}
                required
              />
              {errors.age && (
                <p className="text-red-500 text-xs mt-1">{errors.age}</p>
              )}
            </div>

            {/* Gender */}
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                Gender *
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.gender ? 'border-red-500' : ''}`}
                required
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
              {errors.gender && (
                <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
              )}
            </div>

            {/* Height */}
            <div>
              <label htmlFor="height_cm" className="block text-sm font-medium text-gray-700 mb-1">
                Height (cm) *
              </label>
              <input
                type="number"
                id="height_cm"
                name="height_cm"
                value={formData.height_cm}
                onChange={handleInputChange}
                placeholder="175"
                min="50"
                max="300"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.height_cm ? 'border-red-500' : ''}`}
                required
              />
              {errors.height_cm && (
                <p className="text-red-500 text-xs mt-1">{errors.height_cm}</p>
              )}
            </div>

            {/* Weight */}
            <div>
              <label htmlFor="weight_kg" className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg) *
              </label>
              <input
                type="number"
                id="weight_kg"
                name="weight_kg"
                value={formData.weight_kg}
                onChange={handleInputChange}
                placeholder="70"
                min="20"
                max="300"
                step="0.1"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.weight_kg ? 'border-red-500' : ''}`}
                required
              />
              {errors.weight_kg && (
                <p className="text-red-500 text-xs mt-1">{errors.weight_kg}</p>
              )}
            </div>
          </div>

          {/* BMI Preview */}
          {bmi && (
            <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
              <div className="flex items-center mb-2">
                <Calculator className="w-4 h-4 text-primary-600 mr-2" />
                <h3 className="text-sm font-semibold text-primary-600">
                  BMI Preview
                </h3>
              </div>
              <p className="text-sm text-gray-700">
                Your BMI is <span className="font-semibold">{bmi}</span> - 
                <span className={`font-semibold ml-1 ${bmiInfo.color}`}>
                  {bmiInfo.category}
                </span>
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full flex items-center justify-center py-3 px-4 rounded-lg font-semibold transition-colors ${
              isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving Profile...
              </>
            ) : (
              <>
                Complete Profile
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </button>
        </form>

        <div className="mt-3 p-3 bg-primary-50 rounded-lg">
          <div className="flex items-center mb-2">
            <Shield className="w-4 h-4 text-primary-600 mr-2" />
            <h3 className="text-sm font-semibold text-primary-900">
              Why do we need this information?
            </h3>
          </div>
          <ul className="text-xs text-primary-800 space-y-1">
            <li>• Age and gender provide context for health metrics</li>
            <li>• This information improves the accuracy of our AI analysis</li>
            <li>• Your data is secure and will not be shared with third parties</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default UserDetailsForm
